import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Grid,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  phone: string;
  dni: string;
}

interface Order {
  id: number;
  customer_id: number;
  description: string;
  status: string;
  priority: string;
  estimated_price?: number;
  notes?: string;
  created_by: number;
  assigned_to?: number;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  customer: Customer;
  creator: {
    id: number;
    username: string;
  };
  assignee?: {
    id: number;
    username: string;
  };
}

interface OrderSummary {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_phone: string;
  description: string;
  status: string;
  priority: string;
  estimated_price?: number;
  notes?: string;
  created_at: string;
  created_by: string;
  assigned_to?: string;
}

interface OrdersResponse {
  orders: OrderSummary[];
  total_orders: number;
  pending_orders: number;
  completed_orders: number;
  cancelled_orders: number;
}

const statusColors: Record<string, 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  completed: 'success',
  cancelled: 'error'
};

const priorityColors: Record<string, 'default' | 'primary' | 'warning' | 'error'> = {
  low: 'default',
  normal: 'primary',
  high: 'warning',
  urgent: 'error'
};

const statusLabels: Record<string, string> = {
  pending: 'Pendiente',
  completed: 'Completado',
  cancelled: 'Cancelado'
};

const priorityLabels: Record<string, string> = {
  low: 'Baja',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente'
};

interface OrderDialogState {
  open: boolean;
  mode: 'create' | 'edit';
  order: OrderSummary | null;
}

export default function Orders() {
  const { user } = useAuth();
  const isCollector = user?.role === 'cobrador';
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [orderDialog, setOrderDialog] = useState<OrderDialogState>({ open: false, mode: 'create', order: null });
  const [orderForm, setOrderForm] = useState({
    customer_id: 0,
    description: '',
    priority: 'normal',
    estimated_price: '',
    notes: ''
  });
  const [stats, setStats] = useState({
    total_orders: 0,
    pending_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ordersResponse, customersResponse] = await Promise.all([
        api.get('/orders'),
        api.get('/customers')
      ]);
      
      setOrders(ordersResponse.data.orders);
      setStats(ordersResponse.data);
      setCustomers(customersResponse.data);
    } catch (error) {
      setError('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    try {
      const orderData = {
        ...orderForm,
        estimated_price: orderForm.estimated_price ? parseFloat(orderForm.estimated_price) : null
      };
      
      await api.post('/orders', orderData);
      setOrderDialog({ open: false, mode: 'create', order: null });
      setOrderForm({
        customer_id: 0,
        description: '',
        priority: 'normal',
        estimated_price: '',
        notes: ''
      });
      fetchData();
      setSuccess('Pedido creado exitosamente');
    } catch (error) {
      setError('Error al crear el pedido');
    }
  };

  const handleUpdateOrder = async () => {
    if (!orderDialog.order) return;
    
    try {
      const orderData = {
        ...orderForm,
        estimated_price: orderForm.estimated_price ? parseFloat(orderForm.estimated_price) : null
      };
      
      await api.put(`/orders/${orderDialog.order.id}`, orderData);
      setOrderDialog({ open: false, mode: 'create', order: null });
      setOrderForm({
        customer_id: 0,
        description: '',
        priority: 'normal',
        estimated_price: '',
        notes: ''
      });
      fetchData();
      setSuccess('Pedido actualizado exitosamente');
    } catch (error) {
      setError('Error al actualizar el pedido');
    }
  };

  const handleStatusChange = async (orderId: number, newStatus: string) => {
    if (isCollector) {
      setError('No tienes permisos para actualizar pedidos');
      return;
    }
    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      fetchData();
      setSuccess('Estado del pedido actualizado');
    } catch (error) {
      setError('Error al actualizar el estado');
    }
  };

  const handleDeleteOrder = async (orderId: number) => {
    if (isCollector) {
      setError('No tienes permisos para eliminar pedidos');
      return;
    }
    if (window.confirm('¿Estás seguro de que deseas eliminar este pedido?')) {
      try {
        await api.delete(`/orders/${orderId}`);
        fetchData();
        setSuccess('Pedido eliminado exitosamente');
      } catch (error) {
        setError('Error al eliminar el pedido');
      }
    }
  };

  const openOrderDialog = (mode: 'create' | 'edit', order: OrderSummary | null = null) => {
    if (mode === 'edit' && isCollector) {
      setError('No tienes permisos para editar pedidos');
      return;
    }
    if (mode === 'edit' && order) {
      setOrderForm({
        customer_id: order.customer_id || 0,
        description: order.description,
        priority: order.priority,
        estimated_price: order.estimated_price?.toString() || '',
        notes: order.notes || ''
      });
    } else {
      setOrderForm({
        customer_id: 0,
        description: '',
        priority: 'normal',
        estimated_price: '',
        notes: ''
      });
    }
    setOrderDialog({ open: true, mode, order });
  };

  const filteredOrders = orders.filter(order => {
    switch (tabValue) {
      case 0: return order.status === 'pending';
      case 1: return order.status === 'completed';
      case 2: return order.status === 'cancelled';
      default: return true;
    }
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Cargando pedidos...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Estadísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Pedidos
              </Typography>
              <Typography variant="h4">
                {stats.total_orders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pendientes
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pending_orders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completados
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed_orders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Cancelados
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.cancelled_orders}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botón para crear pedido */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Gestión de Pedidos</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => openOrderDialog('create')}
        >
          Nuevo Pedido
        </Button>
      </Box>

      {/* Tabs para filtrar por estado */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label={`Pendientes (${stats.pending_orders})`} />
          <Tab label={`Completados (${stats.completed_orders})`} />
          <Tab label={`Cancelados (${stats.cancelled_orders})`} />
        </Tabs>
      </Box>

      {/* Tabla de pedidos */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Prioridad</TableCell>
              <TableCell>Precio Est.</TableCell>
              <TableCell>Fecha</TableCell>
              {!isCollector && <TableCell>Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((order) => (
              <TableRow key={order.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {order.customer_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {order.customer_phone}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ maxWidth: 300 }}>
                    {order.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={statusLabels[order.status]}
                    color={statusColors[order.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={priorityLabels[order.priority]}
                    color={priorityColors[order.priority]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {order.estimated_price ? `$${order.estimated_price.toLocaleString()}` : '-'}
                </TableCell>
                <TableCell>
                  {new Date(order.created_at).toLocaleDateString()}
                </TableCell>
                {!isCollector && (
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleStatusChange(order.id, 'pending')}
                      disabled={order.status === 'pending'}
                      color="warning"
                    >
                      <ScheduleIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleStatusChange(order.id, 'completed')}
                      disabled={order.status === 'completed'}
                      color="success"
                    >
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleStatusChange(order.id, 'cancelled')}
                      disabled={order.status === 'cancelled'}
                      color="error"
                    >
                      <CancelIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => openOrderDialog('edit', order)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteOrder(order.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo para crear/editar pedido */}
      <Dialog open={orderDialog.open} onClose={() => setOrderDialog({ open: false, mode: 'create', order: null })} maxWidth="md" fullWidth>
        <DialogTitle>
          {orderDialog.mode === 'create' ? 'Crear Nuevo Pedido' : 'Editar Pedido'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Cliente</InputLabel>
              <Select
                value={orderForm.customer_id}
                onChange={(e) => setOrderForm({ ...orderForm, customer_id: Number(e.target.value) })}
                label="Cliente"
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.last_name} {customer.first_name}
                    {customer.phone ? ` - ${customer.phone}` : ''}
                    {customer.dni ? ` · DNI ${customer.dni}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Descripción del Pedido"
              multiline
              rows={3}
              value={orderForm.description}
              onChange={(e) => setOrderForm({ ...orderForm, description: e.target.value })}
              sx={{ mb: 2 }}
              placeholder="Ej: Pásame cotización de un televisor Samsung 55 pulgadas"
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Prioridad</InputLabel>
              <Select
                value={orderForm.priority}
                onChange={(e) => setOrderForm({ ...orderForm, priority: e.target.value })}
                label="Prioridad"
              >
                <MenuItem value="low">Baja</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">Alta</MenuItem>
                <MenuItem value="urgent">Urgente</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Precio Estimado"
              type="number"
              value={orderForm.estimated_price}
              onChange={(e) => setOrderForm({ ...orderForm, estimated_price: e.target.value })}
              sx={{ mb: 2 }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText="Opcional - Precio estimado si se conoce"
            />

              {/* Campo 'Notas Adicionales' removido a pedido */}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDialog({ open: false, mode: 'create', order: null })}>
            Cancelar
          </Button>
          <Button
            onClick={orderDialog.mode === 'create' ? handleCreateOrder : handleUpdateOrder}
            variant="contained"
          >
            {orderDialog.mode === 'create' ? 'Crear' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
